import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { customerKeys } from '@/hooks/useCustomers'
import type { ApiResponse, PaginatedResponse } from '@/types'

/**
 * EP-160 T160-104w: the operator side of the six PDPA data-subject rights
 * (docs/ep-160-support-access-and-privacy-in-the-app.md §2.7, S6 owner side).
 * Owner and admin only; the tenant files the request in the tenant app.
 */
export type DataSubjectRequestType =
  | 'access'
  | 'erasure'
  | 'rectification'
  | 'restriction'
  | 'objection'
  | 'withdrawal'

export type DataSubjectRequestStatus = 'pending' | 'processing' | 'completed' | 'rejected'

export interface DataSubjectRequest {
  id: number
  customer_id: number
  customer_name: string | null
  request_type: DataSubjectRequestType
  status: DataSubjectRequestStatus
  // The tenant's own text; never overwritten by the decision.
  note: string | null
  // The operator's reason for the decision.
  decision_note: string | null
  requested_at: string
  due_at: string
  processed_at: string | null
  processed_by_id: number | null
  // Access requests only: the export link has been sent to the tenant.
  // The tenant-app payload carries it; the operator payload may not yet.
  export_ready?: boolean
  // EP-162 item 9.2: an owner or admin lifted the restriction this request
  // set; customer_restricted is whether the customer is restricted now
  // (another approved request may keep them so).
  lifted_at?: string | null
  lifted_by_id?: number | null
  lift_reason?: string | null
  customer_restricted?: boolean
}

// Completing one of these restricts the customer; an approved one that is
// not lifted can be lifted (EP-162 item 9.2).
export const RESTRICTING_TYPES: readonly DataSubjectRequestType[] = ['restriction', 'objection', 'withdrawal']

export const isLiftable = (r: DataSubjectRequest): boolean =>
  RESTRICTING_TYPES.includes(r.request_type) && r.status === 'completed' && !r.lifted_at

export const dataSubjectRequestKeys = {
  all: ['dataSubjectRequests'] as const,
  lists: () => [...dataSubjectRequestKeys.all, 'list'] as const,
  list: (page: number) => [...dataSubjectRequestKeys.lists(), page] as const,
}

export function useDataSubjectRequests(page = 1) {
  return useQuery({
    queryKey: dataSubjectRequestKeys.list(page),
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<DataSubjectRequest[]>>(
        '/api/data_subject_requests',
        { params: { page } },
      )
      return data
    },
    placeholderData: keepPreviousData,
  })
}

export interface UpdateDataSubjectRequestInput {
  id: number
  status: 'completed' | 'rejected'
  decision_note?: string
}

export function useUpdateDataSubjectRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateDataSubjectRequestInput) => {
      const { data } = await api.patch<ApiResponse<DataSubjectRequest>>(
        `/api/data_subject_requests/${id}`,
        body,
      )
      return data.data
    },
    onSuccess: (request) => {
      queryClient.invalidateQueries({ queryKey: dataSubjectRequestKeys.lists() })
      // Completing an erasure renames the customer, and a restriction stamps
      // the customer row, so that customer's own screens are stale too.
      if (request.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: customerKeys.detail(request.customer_id) })
        queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      }
    },
    onError: () => {
      // A 422 request_already_decided means the list is out of date.
      queryClient.invalidateQueries({ queryKey: dataSubjectRequestKeys.lists() })
    },
  })
}

export interface LiftDataSubjectRequestInput {
  id: number
  reason: string
}

/** EP-162 item 9.2: POST /api/data_subject_requests/:id/lift (owner, admin). */
export function useLiftDataSubjectRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, reason }: LiftDataSubjectRequestInput) => {
      const { data } = await api.post<ApiResponse<DataSubjectRequest>>(
        `/api/data_subject_requests/${id}/lift`,
        { reason },
      )
      return data.data
    },
    onSuccess: (request) => {
      queryClient.invalidateQueries({ queryKey: dataSubjectRequestKeys.lists() })
      // Lifting clears the customer's restriction stamp.
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(request.customer_id) })
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
    },
    onError: () => {
      // A 422 request_not_liftable means the list is out of date.
      queryClient.invalidateQueries({ queryKey: dataSubjectRequestKeys.lists() })
    },
  })
}
